// US-09 / US-10: bulk-import пинов из списка адресов, CSV или URL объявлений.
//
// Три режима:
//   addresses — одна строка = один адрес, геокодируется через Nominatim
//   csv       — колонки address,price,url; тот же геокодинг
//   urls      — одна строка = URL объявления (Immobiliare, Idealista и др.);
//               координаты извлекаются через /api/extract-url, fallback — Nominatim
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { pins as pinsApi, extractUrl, ApiError } from '@/lib/api';
import { emit } from '@/lib/events';
import { queries } from '@/data';
import { geocode } from './nominatim';
import { buildNearestComuneIndex, findNearestIstat } from './nearest-comune';

type Format = 'addresses' | 'csv' | 'urls';

interface BulkPinInput {
  address?: string;   // addresses / csv mode
  url?: string;       // csv / urls mode
  price?: string;     // csv / urls mode
  label: string;      // display label for the row
}

interface RowState {
  input: BulkPinInput;
  status: 'pending' | 'extracting' | 'geocoding' | 'creating' | 'done' | 'error';
  error?: string;
  lat?: number;
  lon?: number;
}

const MAX_ROWS = 50;

function parseAddressLines(text: string): BulkPinInput[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .map((l) => ({ address: l, label: l }));
}

function parseCsv(text: string): BulkPinInput[] {
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
  if (lines.length === 0) return [];
  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  const addrIdx = header.indexOf('address');
  if (addrIdx === -1) {
    return lines.map((l) => ({ address: l, label: l }));
  }
  const priceIdx = header.indexOf('price');
  const urlIdx = header.indexOf('url');
  const out: BulkPinInput[] = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = lines[i].split(',').map((c) => c.trim());
    const address = cells[addrIdx];
    if (!address) continue;
    out.push({
      address,
      price: priceIdx !== -1 ? cells[priceIdx] : undefined,
      url: urlIdx !== -1 ? cells[urlIdx] : undefined,
      label: address,
    });
  }
  return out;
}

function parseUrlLines(text: string): BulkPinInput[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.startsWith('http://') || l.startsWith('https://'))
    .map((l) => {
      // Show domain + first path segment as label so the list is readable
      let label = l;
      try {
        const u = new URL(l);
        label = u.hostname.replace(/^www\./, '') + (u.pathname !== '/' ? u.pathname.slice(0, 40) : '');
      } catch {
        // keep raw URL
      }
      return { url: l, label };
    });
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function BulkImportDialog({ open, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [format, setFormat] = useState<Format>('urls');
  const [text, setText] = useState('');
  const [rows, setRows] = useState<RowState[]>([]);
  const [running, setRunning] = useState(false);

  // Spatial index for nearest-comune lookup, so imported pins inherit istat.
  const { data: meta } = useQuery(queries.meta());
  const { data: lightIndex } = useQuery({
    ...queries.lightIndex(meta?.version ?? ''),
    enabled: !!meta?.version,
  });
  const nearestIndex = useMemo(() => {
    if (!lightIndex) return null;
    return buildNearestComuneIndex([...lightIndex.byIstat.values()]);
  }, [lightIndex]);

  useEffect(() => {
    if (!open) {
      setText('');
      setRows([]);
      setRunning(false);
    }
  }, [open]);

  const parsed = useMemo(() => {
    if (!text.trim()) return [];
    let items: BulkPinInput[];
    if (format === 'csv') items = parseCsv(text);
    else if (format === 'urls') items = parseUrlLines(text);
    else items = parseAddressLines(text);
    return items.slice(0, MAX_ROWS);
  }, [text, format]);

  const exceedsCap = useMemo(() => {
    let total: number;
    if (format === 'csv') total = parseCsv(text).length;
    else if (format === 'urls') total = parseUrlLines(text).length;
    else total = parseAddressLines(text).length;
    return total > MAX_ROWS;
  }, [text, format]);

  const startImport = async () => {
    if (parsed.length === 0 || running) return;
    setRunning(true);
    const initial: RowState[] = parsed.map((input) => ({ input, status: 'pending' }));
    setRows(initial);

    const bbox: Array<{ lat: number; lon: number }> = [];

    for (let i = 0; i < initial.length; i += 1) {
      const inp = initial[i].input;

      // ── URL mode: extract metadata from the listing page ─────────────────
      if (format === 'urls' && inp.url) {
        setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'extracting' } : r));

        let extracted: Awaited<ReturnType<typeof extractUrl>>;
        try {
          extracted = await extractUrl(inp.url);
        } catch {
          setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'error', error: 'fetch_error' } : r));
          continue;
        }

        let lat = extracted.lat;
        let lon = extracted.lon;

        // Fallback: geocode the extracted address if no coordinates found
        if ((lat === null || lon === null) && extracted.address) {
          setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'geocoding' } : r));
          const hit = await geocode(extracted.address);
          if (hit) { lat = hit.lat; lon = hit.lon; }
        }

        if (lat === null || lon === null) {
          setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'error', error: 'no_location' } : r));
          continue;
        }

        setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'creating', lat: lat!, lon: lon! } : r));

        const istat = nearestIndex ? findNearestIstat(lat, lon, nearestIndex) : null;
        const pinTitle = (extracted.title ?? extracted.address ?? inp.url ?? inp.label).slice(0, 200);
        const pinType = inp.url.includes('idealista') ? 'idealista' as const : 'custom' as const;
        const pinMeta = extracted.price ? { price: extracted.price } : null;

        try {
          await pinsApi.create({
            type: pinType,
            title: pinTitle,
            url: inp.url,
            lat,
            lon,
            countryCode: 'IT',
            comuneIstat: istat,
            metadata: pinMeta,
            notes: null,
          });
          bbox.push({ lat, lon });
          setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'done' } : r));
        } catch (err) {
          setRows((prev) => prev.map((r, idx) =>
            idx === i ? { ...r, status: 'error', error: err instanceof ApiError ? err.message : 'save_failed' } : r,
          ));
        }
        continue;
      }

      // ── Address / CSV mode: geocode via Nominatim ─────────────────────────
      setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'geocoding' } : r));
      const hit = await geocode(inp.address ?? inp.label);
      if (!hit) {
        setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'error', error: 'not_found' } : r));
        continue;
      }
      setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'creating', lat: hit.lat, lon: hit.lon } : r));
      const istat = nearestIndex ? findNearestIstat(hit.lat, hit.lon, nearestIndex) : null;
      try {
        await pinsApi.create({
          type: 'custom',
          title: (inp.address ?? inp.label).slice(0, 200),
          url: inp.url ?? null,
          lat: hit.lat,
          lon: hit.lon,
          countryCode: 'IT',
          comuneIstat: istat,
          metadata: inp.price ? { price: inp.price } : null,
          notes: null,
        });
        bbox.push({ lat: hit.lat, lon: hit.lon });
        setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, status: 'done' } : r));
      } catch (err) {
        setRows((prev) => prev.map((r, idx) =>
          idx === i ? { ...r, status: 'error', error: err instanceof ApiError ? err.message : 'save_failed' } : r,
        ));
      }
    }

    qc.invalidateQueries({ queryKey: ['pins'] });
    if (bbox.length > 0) {
      emit('places:flyToBounds', { points: bbox });
    }
    setRunning(false);
  };

  if (!open) return null;

  const doneCount = rows.filter((r) => r.status === 'done').length;
  const errorCount = rows.filter((r) => r.status === 'error').length;
  const inProgress = rows.length > 0 && rows.length !== doneCount + errorCount;

  const placeholder =
    format === 'csv'
      ? 'address,price,url\nVia Roma 12 Trento,€420k,https://idealista.it/...\nViale Verona 8 Trento,€380k,'
      : format === 'urls'
        ? 'https://www.immobiliare.it/annunci/123456789/\nhttps://www.idealista.it/immobile/98765432/\nhttps://www.casa.it/annuncio/...'
        : 'Via Roma 12, Trento\nViale Verona 8, Trento\nPiazza Duomo, Bolzano';

  const parsedLabel =
    format === 'urls'
      ? t('pins.bulk.parsedUrls', { count: parsed.length, defaultValue: 'Parsed {{count}} URLs' })
      : t('pins.bulk.parsed', { count: parsed.length, defaultValue: 'Parsed {{count}} addresses' });

  const statusLabel = (r: RowState) => {
    if (r.status === 'pending') return '…';
    if (r.status === 'extracting') return t('pins.bulk.statusExtracting', { defaultValue: 'extracting' });
    if (r.status === 'geocoding') return t('pins.bulk.statusGeocoding', { defaultValue: 'geocoding' });
    if (r.status === 'creating') return t('pins.bulk.statusSaving', { defaultValue: 'saving' });
    if (r.status === 'done') return '✓';
    return `✗ ${r.error ?? ''}`;
  };

  const tabCls = (f: Format) =>
    `rounded-md px-3 py-1 ${
      format === f
        ? 'bg-[color:var(--color-ink-100)] text-[color:var(--color-ink-900)]'
        : 'text-[color:var(--color-ink-500)] hover:text-[color:var(--color-ink-900)]'
    }`;

  return (
    <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        onClick={running ? undefined : onClose}
        aria-label="Close dialog"
        tabIndex={-1}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-pin-title"
        className="relative z-10 flex max-h-[90dvh] w-full max-w-xl flex-col rounded-lg bg-[color:var(--color-paper)] p-5 shadow-2xl"
      >
        <h2 id="bulk-pin-title" className="mb-1 font-serif text-lg font-medium text-[color:var(--color-ink-900)]">
          {t('pins.bulk.title', { defaultValue: 'Import a shortlist of places' })}
        </h2>
        <p className="mb-3 text-xs text-[color:var(--color-ink-500)]">
          {format === 'urls'
            ? t('pins.bulk.subtitleUrls', {
                defaultValue:
                  'Paste listing URLs (one per line). We extract coordinates from the page — works with Immobiliare.it, Idealista.it, Casa.it and others.',
              })
            : t('pins.bulk.subtitle', {
                defaultValue:
                  'Paste addresses (one per line) or a CSV. We geocode each with Nominatim — about 1 second per address.',
              })}
        </p>

        {/* Format tabs */}
        <div className="mb-2 flex gap-1 text-xs font-medium">
          <button type="button" onClick={() => setFormat('urls')} aria-pressed={format === 'urls'} className={tabCls('urls')}>
            🔗 {t('pins.bulk.urls', { defaultValue: 'URLs' })}
          </button>
          <button type="button" onClick={() => setFormat('addresses')} aria-pressed={format === 'addresses'} className={tabCls('addresses')}>
            {t('pins.bulk.addresses', { defaultValue: 'Addresses' })}
          </button>
          <button type="button" onClick={() => setFormat('csv')} aria-pressed={format === 'csv'} className={tabCls('csv')}>
            {t('pins.bulk.csv', { defaultValue: 'CSV' })}
          </button>
        </div>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          rows={6}
          disabled={running}
          className="mb-2 w-full rounded-md border border-slate-300 px-3 py-2 font-mono text-xs leading-snug focus:border-[color:var(--color-terra-600)] focus:outline-none disabled:bg-slate-50"
        />

        {parsed.length > 0 && (
          <p className="mb-2 text-xs text-[color:var(--color-ink-500)]">
            {parsedLabel}
            {exceedsCap && (
              <span className="ml-2 text-amber-700">
                {t('pins.bulk.cappedAt', { max: MAX_ROWS, defaultValue: '(limited to first {{max}} per batch)' })}
              </span>
            )}
          </p>
        )}

        {rows.length > 0 && (
          <div className="mb-3 max-h-48 overflow-y-auto rounded-md border border-slate-200 bg-white">
            <ul className="divide-y divide-slate-100 text-xs">
              {rows.map((r, i) => (
                <li key={i} className="flex items-center justify-between gap-2 px-2 py-1">
                  <span className="truncate text-[color:var(--color-ink-700)]">{r.input.label}</span>
                  <span
                    className={`shrink-0 font-mono text-[10px] uppercase ${
                      r.status === 'done'
                        ? 'text-emerald-700'
                        : r.status === 'error'
                          ? 'text-red-700'
                          : 'text-[color:var(--color-ink-400)]'
                    }`}
                  >
                    {statusLabel(r)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {inProgress && (
          <div className="mb-3">
            <div className="h-1.5 w-full overflow-hidden rounded bg-slate-100">
              <div
                className="h-full bg-[color:var(--color-terra-600)] transition-all"
                style={{ width: `${((doneCount + errorCount) / rows.length) * 100}%` }}
              />
            </div>
            <p className="mt-1 text-[10px] text-[color:var(--color-ink-500)]">
              {t('pins.bulk.progress', { done: doneCount + errorCount, total: rows.length, defaultValue: '{{done}} of {{total}}' })}
            </p>
          </div>
        )}

        {rows.length > 0 && !inProgress && (
          <p className="mb-3 text-xs text-[color:var(--color-ink-700)]">
            {t('pins.bulk.summary', { ok: doneCount, fail: errorCount, defaultValue: 'Imported {{ok}}; {{fail}} failed.' })}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={running}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {rows.length > 0 && !inProgress
              ? t('common.close', { defaultValue: 'Close' })
              : t('common.cancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            type="button"
            onClick={startImport}
            disabled={parsed.length === 0 || running}
            className="rounded-md bg-[color:var(--color-terra-600)] px-4 py-1.5 text-sm font-medium text-[color:var(--color-paper)] hover:bg-[color:var(--color-terra-700)] disabled:opacity-50"
          >
            {running
              ? t('pins.bulk.importing', { defaultValue: 'Importing…' })
              : t('pins.bulk.start', { defaultValue: 'Import' })}
          </button>
        </div>
      </div>
    </div>
  );
}
